import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../http/http.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { environment } from '../../../../environments/environment';
import { createClient } from '@supabase/supabase-js';

export interface QuestionTemplate {
  id: string;
  name: string;
  is_default: boolean;
  created_at: string;
}

export interface GenerateQuestionsResponse {
  questions: { question_text: string; question_tag?: string }[];
}

@Injectable({
  providedIn: 'root'
})
export class QuestionTemplateService {
  private readonly _generateQuestionsUrl =
    `${environment.apiUrl}/functions/v1/dash_api_generate_questions`;
  
  private readonly _supabase = createClient(
    environment.apiUrl,
    environment.apiKey
  );

  constructor(
    private readonly _http: HttpService,
    private readonly _auth: AuthenticationService
  ) {}

  /** Fetch all templates for the current user, ordered oldest-first */
  getTemplates(): Observable<QuestionTemplate[]> {
    return this._http.get(
      'rest/v1/question_templates?select=id,name,is_default,created_at&order=created_at.asc'
    );
  }

  /**
   * Create a new template.
   * The Prefer header makes Supabase return the created row so we get the new id.
   * user_id must be supplied explicitly so the RLS WITH CHECK (user_id = auth.uid()) passes.
   */
  createTemplate(name: string, isDefault = false): Observable<QuestionTemplate[]> {
    const userId = this._getUserId();
    return this._http.post(
      'rest/v1/question_templates',
      { name, is_default: isDefault, user_id: userId },
      false,
      { headers: { 'Prefer': 'return=representation' } }
    );
  }

  private _getUserId(): string | null {
    const userDetail = this._auth.getUserDetail();
    return userDetail?.user?.id || (userDetail as any)?.id || null;
  }

  /** Rename an existing template */
  renameTemplate(id: string, name: string): Observable<any> {
    return this._http.patch(
      `rest/v1/question_templates?id=eq.${id}`,
      { name },
      false
    );
  }

  /** Delete a template (questions cascade automatically) */
  deleteTemplate(id: string): Observable<any> {
    return this._http.delete(`rest/v1/question_templates?id=eq.${id}`, false);
  }

  generateQuestions(prompt: string): Observable<GenerateQuestionsResponse> {
    return new Observable<GenerateQuestionsResponse>((observer) => {
      (async () => {
        try {
          const authToken = await this._resolveAuthToken();
          if (!authToken || authToken === 'Bearer undefined' || authToken === 'Bearer null') {
            observer.error(new Error('No valid JWT found. Please sign in again.'));
            return;
          }

          const response = await fetch(this._generateQuestionsUrl, {
            method: 'POST',
            headers: {
              Authorization: authToken,
              apikey: environment.apiKey,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ prompt }),
          });

          const text = await response.text();
          let payload: any = {};
          if (text) {
            try {
              payload = JSON.parse(text);
            } catch {
              payload = { message: text };
            }
          }

          if (!response.ok) {
            const message = payload?.error || payload?.message || `Request failed (${response.status})`;
            throw new Error(message);
          }

          if (!Array.isArray(payload?.questions)) {
            throw new Error('Invalid response format from question generator.');
          }

          observer.next(payload as GenerateQuestionsResponse);
          observer.complete();
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to generate questions.';
          observer.error(new Error(message));
        }
      })();
    });
  }

  private async _resolveAuthToken(): Promise<string> {
    await this._auth.ensureValidSession();

    // First, try to get the session from Supabase auth (returns HS256 tokens)
    try {
      const { data } = await this._supabase.auth.getSession();
      const sessionToken = data.session?.access_token;
      if (sessionToken) {
        const bearer = `Bearer ${sessionToken}`;
        this._logTokenType(bearer);
        console.log('✅ Using Supabase session token (HS256)');
        return bearer;
      }
    } catch (e) {
      console.warn('Could not get Supabase session:', e);
    }

    // Fallback to authentication service methods
    const getterToken = (this._auth.getAuthToken || '').trim();
    if (getterToken) {
      this._logTokenType(getterToken);
      return getterToken;
    }

    const userDetail = this._auth.getUserDetail() as any;
    const accessToken = userDetail?.access_token || userDetail?.token || userDetail?.session?.access_token;
    if (accessToken) {
      const bearer = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
      this._logTokenType(bearer);
      return bearer;
    }

    const stored = sessionStorage.getItem('userInfo') || localStorage.getItem('userInfo');
    if (!stored) {
      return '';
    }

    try {
      const parsed = JSON.parse(stored);
      const token = parsed?.access_token || parsed?.token || parsed?.session?.access_token;
      if (!token) {
        return '';
      }
      const bearer = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      this._logTokenType(bearer);
      return bearer;
    } catch {
      return '';
    }
  }

  private _logTokenType(tokenWithBearer: string): void {
    try {
      const token = tokenWithBearer.replace('Bearer ', '');
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        const alg = this._decodeJwtHeader(token)?.alg;
        console.log('🔍 Token Algorithm:', alg);
        console.log('🔍 Token Issuer:', payload.iss);
        console.log('🔍 Token Subject:', payload.sub);
        if (alg !== 'HS256') {
          console.warn('⚠️ WARNING: Token algorithm is', alg, 'Expected HS256');
        }
      }
    } catch (e) {
      console.warn('Could not parse token type', e);
    }
  }

  private _decodeJwtHeader(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      return JSON.parse(atob(parts[0]));
    } catch {
      return null;
    }
  }
}
